import type { ColorProps } from '../../../utilities/shared/Color';
import type { PortIndicator, PortIndicatorPlacement } from '../../Port/utils';
import { VisuallyHidden } from '../../../common/visually_hidden';
import { IconTooltip, Tooltip } from '../../../components/Tooltip/Tooltip';
import { colors, darkThemeSelector, styled } from '../../../stitches.config';
import { chassisVars } from '../../Chassis/atoms/utils';

const SlotIndicatorsContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  widthAll: `$${chassisVars.slot.width}`,
  heightAll: `$${chassisVars.slot.indicator}`,
  paddingRight: '$1',
});
SlotIndicatorsContainer.displayName = 'SlotIndicatorsContainer';

/**
 * One end of the rail. Both are always rendered — an empty cell is what holds the other on its
 * column.
 *
 * `start` holds PoE and `end` holds uplink, one icon each.
 */
const SlotIndicatorsCell = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',

  variants: {
    placement: {
      start: {
        justifyContent: 'flex-start',
      },
      end: {
        justifyContent: 'flex-end',
        widthAll: `$${chassisVars.slot.indicatorIcon}`,
      },
    },
  },
});
SlotIndicatorsCell.displayName = 'SlotIndicatorsCell';

const slotIndicatorPlacements = ['start', 'end'] as const satisfies PortIndicatorPlacement[];

/**
 * Whether any of these indicators draws on the rail rather than in the caption.
 *
 * A port with neither PoE nor uplink draws no rail. Rows stay level regardless, because a slot packs
 * its glyph and caption to the bottom and any unused height collects above the rail. See
 * `SlotTarget`.
 */
export const hasRailIndicators = (indicators: PortIndicator[] | undefined) =>
  indicators?.some((indicator) =>
    slotIndicatorPlacements.some((placement) => placement === indicator.placement),
  ) ?? false;

/** A shade below the rail's grey. Paired with `variant="inherit"`, or the variant colours it too. */
const mutedIndicatorColor = {
  light: 'disabledNeutralLight',
  dark: 'disabledNeutralDark',
} satisfies ColorProps;

/** Stands in for several indicators at once, one pip each. */
const SlotIndicatorPips = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '$1',
  padding: '$2',
  widthAll: `$${chassisVars.slot.indicatorIcon}`,
  heightAll: `$${chassisVars.slot.indicatorIcon}`,
});
SlotIndicatorPips.displayName = 'SlotIndicatorPips';

// Stepped diagonally so a pair can't read as a colon beside the port number.
const SlotIndicatorPip = styled('div', {
  widthAll: '$4',
  heightAll: '$4',
  borderRadius: '50%',

  '&:nth-child(odd)': { alignSelf: 'flex-start' },
  '&:nth-child(even)': { alignSelf: 'flex-end' },

  background: colors.iconNeutralLight,

  [darkThemeSelector]: {
    background: colors.iconNeutralDark,
  },
});
SlotIndicatorPip.displayName = 'SlotIndicatorPip';

/**
 * One indicator, at the size every position draws them — the rail and the caption have to agree, or
 * a port's bolt wouldn't match the arrow it sits under.
 *
 * Colour is the indicator's to choose and `neutral` is what almost all of them choose, which is the
 * point: a grey rail is a background the one icon reporting something happening right now can stand
 * out from. See `getPortIndicators`.
 */
export function SlotIndicatorIcon({ indicator }: { indicator: PortIndicator }) {
  if (indicator.count !== undefined) {
    return (
      <Tooltip contents={indicator.tooltip}>
        <SlotIndicatorPips>
          {Array.from({ length: indicator.count }, (_, index) => (
            <SlotIndicatorPip key={index} />
          ))}
          <VisuallyHidden>{indicator.tooltip}</VisuallyHidden>
        </SlotIndicatorPips>
      </Tooltip>
    );
  }

  return (
    <IconTooltip
      icon={indicator.icon}
      size={chassisVars.slot.indicatorIcon}
      variant={indicator.muted ? 'inherit' : indicator.variant ?? 'neutral'}
      color={indicator.muted ? mutedIndicatorColor : undefined}
      contents={indicator.tooltip}
    />
  );
}

export type SlotIndicatorsProps = {
  indicators: PortIndicator[];
};

/**
 * The rail of property icons above a slot's glyph, laid out by placement rather than by order.
 *
 * PoE holds the left of every slot and uplink the right, so each reads down a whole row without
 * anyone having to look at an icon. Indicators placed in the `caption` are drawn
 * by `SlotCaption` instead and ignored here, so a caller can hand the rail a port's whole set. See
 * `getPortIndicators`.
 */
export function SlotIndicators({ indicators }: SlotIndicatorsProps) {
  return (
    <SlotIndicatorsContainer>
      {slotIndicatorPlacements.map((placement) => (
        <SlotIndicatorsCell key={placement} placement={placement}>
          {indicators
            .filter((indicator) => indicator.placement === placement)
            .map((indicator) => (
              <SlotIndicatorIcon key={indicator.id} indicator={indicator} />
            ))}
        </SlotIndicatorsCell>
      ))}
    </SlotIndicatorsContainer>
  );
}
